import '@/utils/domTranslateCrashGuard';

import '@ant-design/v5-patch-for-react-19';
import { MsalProvider } from '@azure/msal-react';
import i18next from 'i18next';
import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';

import AuthProvider from './auth/AuthProvider.tsx';
import { getMsalInstance } from './auth/msalInstance.ts';
import RootErrorBoundary from './components/pages/error/RootErrorBoundary.tsx';
import './index.css';
import store from './redux/store.ts';
import { currentEnvironment } from './utils/url.ts';

import { Loader } from '@/apps/dispatcher/components/blocks/loader/Loader.tsx';

const App = lazy(() => import('./App.tsx'));

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
  enabled: currentEnvironment !== 'Localhost',
  environment: currentEnvironment,
  release: import.meta.env.VITE_GIT_VERSION,
  // integrations: [Sentry.browserTracingIntegration()],
  // tracesSampleRate: 1.0,
  beforeSend(event) {
    const exception = event.exception?.values?.[0];
    const errorType = exception?.type ?? '';
    const errorMessage = exception?.value ?? event.message ?? '';
    if (
      (errorType.includes('BrowserAuthError') ||
        errorMessage.includes('BrowserAuthError')) &&
      errorMessage.includes('block_iframe_reload')
    ) {
      return null; // Don't log MSAL iframe reload errors to Sentry
    }
    return event;
  },
});

const msalInstance = getMsalInstance();

msalInstance.initialize().then(() => {
  const rootElement = document.getElementById('root');

  if (rootElement) {
    createRoot(rootElement).render(
      <MsalProvider instance={msalInstance}>
        <I18nextProvider i18n={i18next}>
          <Provider store={store}>
            <AuthProvider>
              <RootErrorBoundary>
                <Suspense fallback={<Loader text=" " />}>
                  <App />
                </Suspense>
              </RootErrorBoundary>
            </AuthProvider>
          </Provider>
        </I18nextProvider>
      </MsalProvider>
    );
  } else {
    console.error('Root element not found');
  }
});
